// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import {
  AlertCircle,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Save,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { InlineDeleteConfirm } from '@/app/components/inline-delete-confirm';
import { PageHeader, EntryCard, FormCard, FieldHeader, DetailLine } from '@/app/components/page-layout';
import { UnsavedChangesGuard } from '@/app/components/unsaved-changes-guard';
import { BulkActionBar, SelectionCheckbox } from '@/app/components/bulk-action-bar';
import type { BulkAction } from '@/app/components/bulk-action-bar';
import { useBulkSelection } from '@/app/hooks/useBulkSelection';
import { useUnsavedChanges } from '@/app/hooks/useUnsavedChanges';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useExperiences,
  useCreateExperience,
  useUpdateExperience,
  useDeleteExperience,
  useBulkDeleteExperiences,
  usePolishExperience,
  experienceSchema,
} from '@/app/features/experience';
import type { ExperienceFormData } from '@/app/features/experience';
import {
  useExperienceDetails,
  useCreateExperienceDetail,
  useBulkCreateExperienceDetails,
  useUpdateExperienceDetail,
  useDeleteExperienceDetail,
  useSuggestExperienceDetails,
  usePolishExperienceDetail,
  cleanDetailContent,
} from '@/app/features/experience-detail';
import { apiClient } from '@/lib/api';

type WorkExperience = {
  id: string;
  job_title: string;
  company_name: string;
  location?: string;
  start_date: string;
  end_date?: string;
  description?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};



// --- Detail Lines Section (rendered inside each experience card) ---

function ExperienceDetailLines({ experienceId }: { experienceId: string }) {
  const details = useExperienceDetails(experienceId);
  const createDetail = useCreateExperienceDetail(experienceId);
  const bulkCreate = useBulkCreateExperienceDetails(experienceId);
  const updateDetail = useUpdateExperienceDetail(experienceId);
  const deleteDetail = useDeleteExperienceDetail(experienceId);
  const suggestDetails = useSuggestExperienceDetails(experienceId);
  const polishDetail = usePolishExperienceDetail(experienceId);


  const [newLine, setNewLine] = useState('');
  const [editingDetailId, setEditingDetailId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkContent, setBulkContent] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(
    new Set(),
  );
  // Per-line polish state: maps detail ID -> polished text (null = loading)
  const [polishedLines, setPolishedLines] = useState<
    Record<string, string | null>
  >({});
  // Per-line polish errors: maps detail ID -> error message
  const [polishErrors, setPolishErrors] = useState<Record<string, string>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the suggestions panel when it becomes visible.
  useEffect(() => {
    if (showSuggestions && suggestionsRef.current) {
      suggestionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [showSuggestions, suggestDetails.isPending, suggestDetails.data]);

  function handleQuickAdd(e?: React.SyntheticEvent) {
    e?.preventDefault();
    e?.stopPropagation();
    const content = cleanDetailContent(newLine);
    if (!content) return;

    createDetail.mutate(content, {
      onSuccess: () => {
        setNewLine('');
        inputRef.current?.focus();
      },
    });
  }

  function startEdit(detailId: string, content: string) {
    setEditingDetailId(detailId);
    setEditingContent(content);
  }

  function saveEdit() {
    const cleaned = cleanDetailContent(editingContent);
    if (!editingDetailId || !cleaned) return;
    updateDetail.mutate(
      { detailId: editingDetailId, content: cleaned },
      {
        onSuccess: () => {
          setEditingDetailId(null);
          setEditingContent('');
        },
      },
    );
  }

  function cancelEdit() {
    setEditingDetailId(null);
    setEditingContent('');
  }

  function handleBulkAdd() {
    const lines = bulkContent
      .split('\n')
      .map((l) => cleanDetailContent(l))
      .filter(Boolean);
    if (lines.length === 0) return;

    bulkCreate.mutate(lines, {
      onSuccess: () => {
        setBulkContent('');
        setShowBulkModal(false);
      },
    });
  }

  function handleSuggest() {
    setShowSuggestions(true);
    setSelectedSuggestions(new Set());
    suggestDetails.mutate(undefined);
  }

  function toggleSuggestion(index: number) {
    setSelectedSuggestions((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  function addSelectedSuggestions() {
    const suggestions = suggestDetails.data ?? [];
    const contents = Array.from(selectedSuggestions)
      .map((i) => suggestions[i]?.content)
      .filter(Boolean) as string[];
    if (contents.length === 0) return;

    bulkCreate.mutate(contents, {
      onSuccess: () => {
        setShowSuggestions(false);
        setSelectedSuggestions(new Set());
      },
    });
  }

  function handlePolish(detailId: string) {
    // Mark as loading.
    setPolishedLines((prev) => ({ ...prev, [detailId]: null }));
    setPolishErrors((prev) => { const next = { ...prev }; delete next[detailId]; return next; });
    polishDetail.mutate(
      { detailId },
      {
        onSuccess: (result) => {
          setPolishedLines((prev) => ({
            ...prev,
            [detailId]: (result as { polished: string }).polished,
          }));
        },
        onError: (err) => {
          // Remove the loading state on error.
          setPolishedLines((prev) => {
            const next = { ...prev };
            delete next[detailId];
            return next;
          });
          const msg = err instanceof Error ? err.message : 'Polish failed';
          setPolishErrors((prev) => ({ ...prev, [detailId]: msg }));
          setTimeout(() => setPolishErrors((prev) => { const next = { ...prev }; delete next[detailId]; return next; }), 8000);
        },
      },
    );
  }

  function acceptPolish(detailId: string) {
    const polished = polishedLines[detailId];
    if (!polished) return;
    updateDetail.mutate(
      { detailId, content: polished },
      {
        onSuccess: () => {
          setPolishedLines((prev) => {
            const next = { ...prev };
            delete next[detailId];
            return next;
          });
        },
      },
    );
  }

  function dismissPolish(detailId: string) {
    setPolishedLines((prev) => {
      const next = { ...prev };
      delete next[detailId];
      return next;
    });
  }

  const detailList = (details.data ?? []) as Array<{
    id: string;
    content: string;
    sort_order: number;
  }>;

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-[13px] font-medium uppercase tracking-wide text-muted-foreground">
            Role Highlights
          </p>
          <span className="inline-flex min-w-5 items-center justify-center rounded-full border border-border bg-background px-1.5 py-0.5 text-[11px] font-semibold text-foreground">
            {detailList.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleSuggest}
            disabled={suggestDetails.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2.5 sm:py-1 sm:text-[12px] font-medium text-ai-foreground shadow-sm transition-colors hover:bg-ai/85 focus:outline-none focus:ring-2 focus:ring-ai focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50"
            title="AI Suggest ($0.05)"
          >
            {suggestDetails.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3" />
            )}
            AI Suggest
          </button>
          <button
            type="button"
            onClick={() => setShowBulkModal(true)}
            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <Plus className="h-3 w-3" />
            Bulk Add
          </button>
        </div>
      </div>

      {/* AI Suggestions panel — positioned right below the header so it's
          immediately visible when the user clicks "AI Suggest" */}
      {showSuggestions && (
        <div ref={suggestionsRef} className="mt-3 rounded-md border border-foreground bg-muted/30 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-ai" />
              <span className="text-[13px] font-medium text-foreground">
                AI Suggestions
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowSuggestions(false)}
              className="rounded-md p-2 sm:p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Close suggestions"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {suggestDetails.isPending ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="ml-2 text-[13px] text-muted-foreground">
                Generating suggestions...
              </span>
            </div>
          ) : suggestDetails.error ? (
            <p className="mt-2 text-[13px] text-destructive">
              {suggestDetails.error.message}
            </p>
          ) : suggestDetails.data && suggestDetails.data.length > 0 ? (
            <>
              <ul className="mt-2 space-y-1">
                {suggestDetails.data.map(
                  (s: { content: string }, i: number) => (
                    <li key={i}>
                      <label className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/50">
                        <input
                          type="checkbox"
                          checked={selectedSuggestions.has(i)}
                          onChange={() => toggleSuggestion(i)}
                          className="mt-0.5 h-3.5 w-3.5 rounded border-input accent-primary"
                        />
                        <span className="text-[14px] leading-relaxed text-foreground">
                          {s.content}
                        </span>
                      </label>
                    </li>
                  ),
                )}
              </ul>
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowSuggestions(false)}
                  className="rounded-md px-2.5 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={addSelectedSuggestions}
                  disabled={
                    selectedSuggestions.size === 0 || bulkCreate.isPending
                  }
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                  {bulkCreate.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                  Add Selected ({selectedSuggestions.size})
                </button>
              </div>
            </>
          ) : (
            <p className="mt-2 text-[13px] text-muted-foreground">
              No suggestions generated.
            </p>
          )}
        </div>
      )}

      {/* Detail lines list */}
      {details.isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : detailList.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {detailList.map((detail) => (
              <li
                key={detail.id}
                className="group/detail rounded-md px-2 py-1.5 transition-colors hover:bg-muted/50"
              >
                {editingDetailId === detail.id ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          saveEdit();
                        }
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-[14px] text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={saveEdit}
                      disabled={updateDetail.isPending}
                      className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                      aria-label="Save"
                    >
                      {updateDetail.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Save className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      aria-label="Cancel"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="sm:flex sm:items-start sm:gap-2">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
                        <span className="min-w-0 flex-1 text-[14px] leading-relaxed text-foreground">
                          {detail.content}
                        </span>
                      </div>
                      {/* Desktop: actions inline right */}
                      <div className="hidden shrink-0 items-center gap-0.5 sm:flex">
                        <button
                          type="button"
                          onClick={() => handlePolish(detail.id)}
                          disabled={polishedLines[detail.id] === null}
                          className="rounded-md border border-ai-border/40 bg-ai-muted p-1.5 text-ai transition-colors hover:bg-ai hover:text-ai-foreground focus:outline-none focus:ring-2 focus:ring-ai disabled:opacity-50"
                          aria-label="AI polish this line"
                          title="AI polish"
                        >
                          {polishedLines[detail.id] === null ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Sparkles className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => startEdit(detail.id, detail.content)}
                          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          aria-label="Edit detail"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <InlineDeleteConfirm
                          onConfirm={() => deleteDetail.mutate(detail.id)}
                          isDeleting={deleteDetail.isPending}
                          ariaLabel="Delete detail"
                        />
                      </div>
                    </div>
                    {/* Mobile: actions below */}
                    <div className="mt-1.5 flex items-center gap-1 sm:hidden">
                      <button
                        type="button"
                        onClick={() => handlePolish(detail.id)}
                        disabled={polishedLines[detail.id] === null}
                        className="rounded-md border border-ai-border/40 bg-ai-muted p-2.5 text-ai transition-colors hover:bg-ai hover:text-ai-foreground focus:outline-none focus:ring-2 focus:ring-ai disabled:opacity-50"
                        aria-label="AI polish this line"
                        title="AI polish"
                      >
                        {polishedLines[detail.id] === null ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => startEdit(detail.id, detail.content)}
                        className="rounded-md p-2.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        aria-label="Edit detail"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <InlineDeleteConfirm
                        onConfirm={() => deleteDetail.mutate(detail.id)}
                        isDeleting={deleteDetail.isPending}
                        ariaLabel="Delete detail"
                      />
                    </div>

                  {/* Inline AI polish suggestion */}
                  {detail.id in polishedLines && polishedLines[detail.id] !== undefined && (
                    polishedLines[detail.id] === null ? (
                      <div className="mt-1.5 flex items-center gap-1.5 rounded-md border border-ai-border/30 bg-ai-muted px-2.5 py-2">
                        <Loader2 className="h-3 w-3 animate-spin text-ai" />
                        <span className="text-[13px] text-muted-foreground">
                          Polishing...
                        </span>
                      </div>
                    ) : (
                      <div className="mt-1.5 rounded-md border border-ai-border/30 bg-ai-muted px-2.5 py-2">
                        <div className="flex items-center gap-1.5">
                          <Sparkles className="h-3 w-3 shrink-0 text-ai" />
                          <span className="text-[12px] font-medium uppercase tracking-wide text-ai">
                            AI suggestion
                          </span>
                        </div>
                        <p className="mt-1 text-[14px] leading-relaxed text-foreground">
                          {polishedLines[detail.id]}
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => acceptPolish(detail.id)}
                            disabled={updateDetail.isPending}
                            className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-ai-foreground transition-colors hover:bg-ai/90 disabled:opacity-50"
                          >
                            {updateDetail.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <CheckCircle2 className="h-3 w-3" />
                            )}
                            Use this
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePolish(detail.id)}
                            disabled={polishedLines[detail.id] === null}
                            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                          >
                             <Sparkles className="h-3 w-3" />
                            Regenerate
                          </button>
                          <button
                            type="button"
                            onClick={() => dismissPolish(detail.id)}
                            className="rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )
                  )}

                  {/* Inline AI polish error */}
                  {polishErrors[detail.id] && (
                    <div className="mt-1.5 flex items-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-2">
                      <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />
                      <span className="text-[13px] text-destructive">
                        {polishErrors[detail.id]}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-[13px] text-muted-foreground/60">
          No details added yet. Add what you did at this job to help the AI
          tailor your CV.
        </p>
      )}

      {/* Quick add error */}
      {createDetail.error && (
        <p className="mt-1 text-[13px] text-destructive">
          Failed to add: {createDetail.error.message}
        </p>
      )}

      {/* Quick add input — uses div instead of form to avoid nested form issues */}
      <div className="mt-2 flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={newLine}
          onChange={(e) => setNewLine(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              handleQuickAdd(e);
            }
          }}
          placeholder="Add what you did here... (press Enter)"
          className="flex-1 rounded-md border border-dashed border-input bg-transparent px-2.5 py-1.5 text-[14px] text-foreground placeholder:text-muted-foreground/50 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
        />
        {newLine.trim() && (
          <button
            type="button"
            onClick={handleQuickAdd}
            disabled={createDetail.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {createDetail.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Plus className="h-3 w-3" />
            )}
            Add
          </button>
        )}
      </div>

      {/* Bulk add modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-lg rounded-lg border border-border bg-card-solid p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-[16px] font-semibold text-foreground">
                Bulk Add Details
              </h3>
              <button
                type="button"
                onClick={() => setShowBulkModal(false)}
                className="rounded-md p-2 sm:p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-[14px] text-muted-foreground">
              Enter one item per line. Each line becomes a separate detail entry.
            </p>
            <textarea
              value={bulkContent}
              onChange={(e) => setBulkContent(e.target.value)}
              rows={8}
              className="mt-3 block w-full rounded-md border border-input bg-background px-3 py-2 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="Managed a team of 8 engineers&#10;Designed microservices architecture&#10;Implemented CI/CD pipelines&#10;Conducted code reviews for 3 teams"
              autoFocus
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowBulkModal(false)}
                className="rounded-md px-3 py-2 text-[14px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkAdd}
                disabled={bulkCreate.isPending || !bulkContent.trim()}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {bulkCreate.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5" />
                )}
                Add Lines
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// --- Pending Detail Lines (for create form — local state only, not yet saved) ---

function PendingDetailLines({
  lines,
  onChange,
}: {
  lines: string[];
  onChange: (lines: string[]) => void;
}) {
  const [newLine, setNewLine] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkContent, setBulkContent] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // Per-line polish state: maps line index -> polished text (null = loading)
  const polishMutation = usePolishExperience();
  const [polishedLines, setPolishedLines] = useState<
    Record<number, string | null>
  >({});
  // Per-line polish errors: maps line index -> error message
  const [polishErrors, setPolishErrors] = useState<Record<number, string>>({});

  function handlePolishLine(index: number) {
    setPolishedLines((prev) => ({ ...prev, [index]: null }));
    setPolishErrors((prev) => { const next = { ...prev }; delete next[index]; return next; });
    polishMutation.mutate(
      { description: lines[index] },
      {
        onSuccess: (result) => {
          setPolishedLines((prev) => ({
            ...prev,
            [index]: (result as { polished: string }).polished,
          }));
        },
        onError: (err) => {
          setPolishedLines((prev) => {
            const next = { ...prev };
            delete next[index];
            return next;
          });
          const msg = err instanceof Error ? err.message : 'Polish failed';
          setPolishErrors((prev) => ({ ...prev, [index]: msg }));
          setTimeout(() => setPolishErrors((prev) => { const next = { ...prev }; delete next[index]; return next; }), 8000);
        },
      },
    );
  }

  function acceptPolishLine(index: number) {
    const polished = polishedLines[index];
    if (!polished) return;
    const updated = [...lines];
    updated[index] = polished;
    onChange(updated);
    setPolishedLines((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }

  function dismissPolishLine(index: number) {
    setPolishedLines((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }

  function handleQuickAdd(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    const content = cleanDetailContent(newLine);
    if (!content) return;
    onChange([...lines, content]);
    setNewLine('');
    inputRef.current?.focus();
  }

  function startEdit(index: number) {
    setEditingIndex(index);
    setEditingContent(lines[index] ?? '');
  }

  function saveEdit() {
    const cleaned = cleanDetailContent(editingContent);
    if (editingIndex === null || !cleaned) return;
    const updated = [...lines];
    updated[editingIndex] = cleaned;
    onChange(updated);
    setEditingIndex(null);
    setEditingContent('');
  }

  function cancelEdit() {
    setEditingIndex(null);
    setEditingContent('');
  }

  function removeLine(index: number) {
    onChange(lines.filter((_, i) => i !== index));
  }

  function handleBulkAdd() {
    const newLines = bulkContent
      .split('\n')
      .map((l) => cleanDetailContent(l))
      .filter(Boolean);
    if (newLines.length === 0) return;
    onChange([...lines, ...newLines]);
    setBulkContent('');
    setShowBulkModal(false);
  }

  return (
    <div className="mt-4 rounded-md border border-border bg-muted/20 p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-[13px] font-medium uppercase tracking-wide text-muted-foreground">
            Role Highlights
          </p>
          <span className="inline-flex min-w-5 items-center justify-center rounded-full border border-border bg-background px-1.5 py-0.5 text-[11px] font-semibold text-foreground">
            {lines.length}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setShowBulkModal(true)}
          className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <Plus className="h-3 w-3" />
          Bulk Add
        </button>
      </div>

      {lines.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {lines.map((line, index) => (
            <li
              key={index}
              className="group/detail rounded-md px-2 py-1.5 transition-colors hover:bg-muted/50"
            >
              {editingIndex === index ? (
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        saveEdit();
                      }
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-[14px] text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={saveEdit}
                    className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus:outline-none focus:ring-2 focus:ring-ring"
                    aria-label="Save"
                  >
                    <Save className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    aria-label="Cancel"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div>
                  <div className="sm:flex sm:items-start sm:gap-2">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
                      <span className="min-w-0 flex-1 text-[14px] leading-relaxed text-foreground">
                        {line}
                      </span>
                    </div>
                    {/* Desktop: actions inline right */}
                    <div className="hidden shrink-0 items-center gap-0.5 sm:flex">
                      <button
                        type="button"
                        onClick={() => handlePolishLine(index)}
                        disabled={polishedLines[index] === null}
                        className="rounded-md border border-ai-border/40 bg-ai-muted p-1.5 text-ai transition-colors hover:bg-ai hover:text-ai-foreground focus:outline-none focus:ring-2 focus:ring-ai disabled:opacity-50"
                        aria-label="AI polish this line"
                        title="AI polish"
                      >
                        {polishedLines[index] === null ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => startEdit(index)}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        aria-label="Edit detail"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeLine(index)}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring"
                        aria-label="Remove detail"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  {/* Mobile: actions below */}
                  <div className="mt-1.5 flex items-center gap-1 sm:hidden">
                    <button
                      type="button"
                      onClick={() => handlePolishLine(index)}
                      disabled={polishedLines[index] === null}
                      className="rounded-md border border-ai-border/40 bg-ai-muted p-2.5 text-ai transition-colors hover:bg-ai hover:text-ai-foreground focus:outline-none focus:ring-2 focus:ring-ai disabled:opacity-50"
                      aria-label="AI polish this line"
                      title="AI polish"
                    >
                      {polishedLines[index] === null ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => startEdit(index)}
                      className="rounded-md p-2.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      aria-label="Edit detail"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeLine(index)}
                      className="rounded-md p-2.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring"
                      aria-label="Remove detail"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Inline AI polish suggestion */}
                  {index in polishedLines && polishedLines[index] !== undefined && (
                    polishedLines[index] === null ? (
                      <div className="mt-1.5 flex items-center gap-1.5 rounded-md border border-ai-border/30 bg-ai-muted px-2.5 py-2">
                        <Loader2 className="h-3 w-3 animate-spin text-ai" />
                        <span className="text-[13px] text-muted-foreground">
                          Polishing...
                        </span>
                      </div>
                    ) : (
                      <div className="mt-1.5 rounded-md border border-ai-border/30 bg-ai-muted px-2.5 py-2">
                        <div className="flex items-center gap-1.5">
                          <Sparkles className="h-3 w-3 shrink-0 text-ai" />
                          <span className="text-[12px] font-medium uppercase tracking-wide text-ai">
                            AI suggestion
                          </span>
                        </div>
                        <p className="mt-1 text-[14px] leading-relaxed text-foreground">
                          {polishedLines[index]}
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => acceptPolishLine(index)}
                            className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-ai-foreground transition-colors hover:bg-ai/90"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Use this
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePolishLine(index)}
                            disabled={polishedLines[index] === null}
                            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                          >
                            <Sparkles className="h-3 w-3" />
                            Regenerate
                          </button>
                          <button
                            type="button"
                            onClick={() => dismissPolishLine(index)}
                            className="rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          >
                             Dismiss
                          </button>
                        </div>
                      </div>
                    )
                  )}

                  {/* Inline AI polish error */}
                  {polishErrors[index] && (
                    <div className="mt-1.5 flex items-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-2">
                      <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />
                      <span className="text-[13px] text-destructive">
                        {polishErrors[index]}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-[13px] text-muted-foreground/60">
          No details yet. Add what you did at this job to help the AI tailor
          your CV.
        </p>
      )}

      {/* Quick add input — separate mini-form to prevent parent form submission */}
      <div
        className="mt-2 flex items-center gap-2"
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.stopPropagation();
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={newLine}
          onChange={(e) => setNewLine(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              const content = cleanDetailContent(newLine);
              if (!content) return;
              onChange([...lines, content]);
              setNewLine('');
              inputRef.current?.focus();
            }
          }}
          placeholder="Add what you did here... (press Enter)"
          className="flex-1 rounded-md border border-dashed border-input bg-transparent px-2.5 py-1.5 text-[14px] text-foreground placeholder:text-muted-foreground/50 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
        />
        {newLine.trim() && (
          <button
            type="button"
            onClick={handleQuickAdd}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            <Plus className="h-3 w-3" />
            Add
          </button>
        )}
      </div>

      {/* Bulk add modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-lg rounded-lg border border-border bg-card-solid p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-[16px] font-semibold text-foreground">
                Bulk Add Details
              </h3>
              <button
                type="button"
                onClick={() => setShowBulkModal(false)}
                className="rounded-md p-2 sm:p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-[14px] text-muted-foreground">
              Enter one item per line. Each line becomes a separate detail
              entry.
            </p>
            <textarea
              value={bulkContent}
              onChange={(e) => setBulkContent(e.target.value)}
              rows={8}
              className="mt-3 block w-full rounded-md border border-input bg-background px-3 py-2 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="Managed a team of 8 engineers&#10;Designed microservices architecture&#10;Implemented CI/CD pipelines&#10;Conducted code reviews for 3 teams"
              autoFocus
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowBulkModal(false)}
                className="rounded-md px-3 py-2 text-[14px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkAdd}
                disabled={!bulkContent.trim()}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Lines
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Main Experience Page ---

export function ExperiencePage() {
  const experiences = useExperiences();
  const createExperience = useCreateExperience();
  const updateExperience = useUpdateExperience();
  const deleteExperience = useDeleteExperience();
  const bulkDeleteExperiences = useBulkDeleteExperiences();
  const polishExperience = usePolishExperience();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pendingDetails, setPendingDetails] = useState<string[]>([]);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [formPolishing, setFormPolishing] = useState(false);
  const [formPolishResult, setFormPolishResult] = useState<string | null>(null);
  const [includeDetailLines, setIncludeDetailLines] = useState(false);
  const [formData, setFormData] = useState<ExperienceFormData>({
    job_title: '',
    company_name: '',
    location: '',
    start_date: '',
    end_date: '',
    description: '',
  });
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof ExperienceFormData, string>>
  >({});

  const unsaved = useUnsavedChanges(formData, { enabled: !!editingId });

  // Per-experience polish state: maps ID -> polished text (null = loading)
  const [polishedTexts, setPolishedTexts] = useState<
    Record<string, string | null>
  >({});
  // Per-experience polish errors: maps ID -> error message
  const [polishCardErrors, setPolishCardErrors] = useState<Record<string, string>>({});

  // Per-card include-detail-lines checkbox state.
  const [cardIncludeDetails, setCardIncludeDetails] = useState<
    Record<string, boolean>
  >({});

  const resetForm = useCallback(() => {
    setFormData({
      job_title: '',
      company_name: '',
      location: '',
      start_date: '',
      end_date: '',
      description: '',
    });
    setFieldErrors({});
    setPendingDetails([]);
    setFormPolishing(false);
    setFormPolishResult(null);
    setIncludeDetailLines(false);
    setEditingId(null);
    setIsFormOpen(false);
    unsaved.clear();
  }, [unsaved]);

  // --- Per-card polish ---

  function handlePolishCard(id: string) {
    setPolishedTexts((prev) => ({ ...prev, [id]: null }));
    setPolishCardErrors((prev) => { const next = { ...prev }; delete next[id]; return next; });
    polishExperience.mutate(
      { id, includeDetailLines: !!cardIncludeDetails[id] },
      {
        onSuccess: (result) => {
          setPolishedTexts((prev) => ({
            ...prev,
            [id]: (result as { polished: string }).polished,
          }));
        },
        onError: (err) => {
          setPolishedTexts((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          const msg = err instanceof Error ? err.message : 'Polish failed';
          setPolishCardErrors((prev) => ({ ...prev, [id]: msg }));
          setTimeout(() => setPolishCardErrors((prev) => { const next = { ...prev }; delete next[id]; return next; }), 8000);
        },
      },
    );
  }

  function handleAcceptPolishCard(id: string) {
    const polished = polishedTexts[id];
    if (!polished) return;

    const expList = (experiences.data ?? []) as WorkExperience[];
    const exp = expList.find((e) => e.id === id);
    if (!exp) return;

    // Always replace the description directly with the AI result.
    updateExperience.mutate(
      {
        id,
        job_title: exp.job_title,
        company_name: exp.company_name,
        location: exp.location ?? '',
        start_date: exp.start_date,
        end_date: exp.end_date ?? '',
        description: polished,
      },
      {
        onSuccess: () => {
          setPolishedTexts((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        },
      },
    );
  }

  function dismissPolishCard(id: string) {
    setPolishedTexts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function openCreateForm() {
    resetForm();
    setIsFormOpen(true);
  }

  function openEditForm(experience: WorkExperience) {
    const data: ExperienceFormData = {
      job_title: experience.job_title,
      company_name: experience.company_name,
      location: experience.location ?? '',
      start_date: experience.start_date,
      end_date: experience.end_date ?? '',
      description: experience.description ?? '',
    };
    setFormData(data);
    unsaved.snapshot(data);
    setFieldErrors({});
    setEditingId(experience.id);
    setIsFormOpen(true);
  }

  function handleCloseForm() {
    unsaved.guardClose(resetForm);
  }

  function handleChange(field: keyof ExperienceFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});

    const result = experienceSchema.safeParse(formData);
    if (!result.success) {
      const errors: Partial<Record<keyof ExperienceFormData, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof ExperienceFormData;
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    const finish = () => {
      resetForm();
      toast.success(
        editingId
          ? 'Work experience updated successfully.'
          : 'Work experience saved successfully.',
      );
    };

    if (editingId) {
      // Edit mode: experience details are managed via the live API in the
      // embedded ExperienceDetailLines component, so just update the experience.
      updateExperience.mutate(
        { ...result.data, id: editingId },
        { onSuccess: finish },
      );
    } else {
      // Create mode: save the experience first, then bulk-create pending details.
      createExperience.mutate(result.data, {
        onSuccess: async (created) => {
          const createdId = (created as { id: string }).id;

          if (pendingDetails.length > 0 && createdId) {
            setIsSavingDetails(true);
            try {
              const items = pendingDetails.map((content) => ({ content }));
              await apiClient.POST(
                '/api/v1/experiences/{id}/details/bulk',
                {
                  params: { path: { id: createdId } },
                  body: { items },
                },
              );
            } catch {
              // Details failed but experience was created — user can add them later.
            } finally {
              setIsSavingDetails(false);
            }
          }

          finish();
        },
      });
    }
  }

  function handleDelete(id: string) {
    setDeletingId(id);
    deleteExperience.mutate(id, {
      onSuccess: () => {
        setDeletingId(null);
        if (editingId === id) {
          resetForm();
        }
        if (expandedId === id) {
          setExpandedId(null);
        }
      },
      onError: () => {
        setDeletingId(null);
      },
    });
  }

  function toggleExpand(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  const isPending =
    createExperience.isPending || updateExperience.isPending || isSavingDetails;

  const experienceList = (experiences.data ?? []) as WorkExperience[];

  const allExpIds = useMemo(() => experienceList.map((e) => e.id), [experienceList]);
  const bulk = useBulkSelection(allExpIds);
  const inSelectionMode = !bulk.noneSelected;

  const bulkActions: BulkAction[] = useMemo(
    () => [
      {
        key: 'delete',
        label: 'Delete',
        icon: Trash2,
        destructive: true,
        onExecute: async (ids) => {
          await bulkDeleteExperiences.mutateAsync(ids);
        },
      },
    ],
    [bulkDeleteExperiences],
  );

  const experienceForm = (
    <FormCard className={editingId ? 'mt-0 sm:mt-0' : undefined}>
      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-semibold text-foreground">
          {editingId ? 'Edit Experience' : 'New Experience'}
        </h2>
        <button
          type="button"
          onClick={handleCloseForm}
          className="rounded-md p-2 sm:p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Close form"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {(createExperience.error ?? updateExperience.error) && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          {(createExperience.error ?? updateExperience.error)?.message}
        </div>
      )}

      <form
        className="mt-4 space-y-4"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="job-title"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              Job title
            </label>
            <input
              id="job-title"
              type="text"
              value={formData.job_title}
              onChange={(e) => handleChange('job_title', e.target.value)}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="Senior Software Engineer"
              aria-invalid={!!fieldErrors.job_title}
              aria-describedby={
                fieldErrors.job_title ? 'job-title-error' : undefined
              }
            />
            {fieldErrors.job_title && (
              <p
                id="job-title-error"
                className="mt-1 text-[13px] text-destructive"
              >
                {fieldErrors.job_title}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="company-name"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              Company
            </label>
            <input
              id="company-name"
              type="text"
              value={formData.company_name}
              onChange={(e) =>
                handleChange('company_name', e.target.value)
              }
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="Acme Corp"
              aria-invalid={!!fieldErrors.company_name}
              aria-describedby={
                fieldErrors.company_name
                  ? 'company-name-error'
                  : undefined
              }
            />
            {fieldErrors.company_name && (
              <p
                id="company-name-error"
                className="mt-1 text-[13px] text-destructive"
              >
                {fieldErrors.company_name}
              </p>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="location"
            className="mb-1.5 block text-[14px] font-medium text-foreground"
          >
            Location{' '}
            <span className="font-normal text-muted-foreground">
              (optional)
            </span>
          </label>
          <input
            id="location"
            type="text"
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder="San Francisco, CA"
            aria-invalid={!!fieldErrors.location}
            aria-describedby={
              fieldErrors.location ? 'location-error' : undefined
            }
          />
          {fieldErrors.location && (
            <p
              id="location-error"
              className="mt-1 text-[13px] text-destructive"
            >
              {fieldErrors.location}
            </p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="start-date"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              Start date
            </label>
            <input
              id="start-date"
              type="text"
              placeholder="e.g. 2020, Jun 2020, 2020-06-15"
              value={formData.start_date}
              onChange={(e) =>
                handleChange('start_date', e.target.value)
              }
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              aria-invalid={!!fieldErrors.start_date}
              aria-describedby={
                fieldErrors.start_date ? 'start-date-error' : undefined
              }
            />
            {fieldErrors.start_date && (
              <p
                id="start-date-error"
                className="mt-1 text-[13px] text-destructive"
              >
                {fieldErrors.start_date}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="end-date"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              End date{' '}
              <span className="font-normal text-muted-foreground">
                (leave empty for current)
              </span>
            </label>
            <input
              id="end-date"
              type="text"
              placeholder="e.g. 2024, Dec 2024 (empty = current)"
              value={formData.end_date}
              onChange={(e) => handleChange('end_date', e.target.value)}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              aria-invalid={!!fieldErrors.end_date}
              aria-describedby={
                fieldErrors.end_date ? 'end-date-error' : undefined
              }
            />
            {fieldErrors.end_date && (
              <p
                id="end-date-error"
                className="mt-1 text-[13px] text-destructive"
              >
                {fieldErrors.end_date}
              </p>
            )}
          </div>
        </div>

        <div>
          <FieldHeader htmlFor="description" label="Description" hint="(optional)">
              <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={includeDetailLines}
                  onChange={(e) => setIncludeDetailLines(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-input accent-primary"
                />
                Include role highlights
              </label>
              <button
                type="button"
                disabled={
                  (!(formData.description ?? '').trim() && !includeDetailLines) ||
                  polishExperience.isPending ||
                  formPolishing
                }
                onClick={() => {
                  setFormPolishing(true);
                  setFormPolishResult(null);
                  const params: {
                    id?: string;
                    description?: string;
                    includeDetailLines?: boolean;
                  } = editingId
                    ? { id: editingId, description: formData.description || undefined, includeDetailLines }
                    : {
                          description: formData.description,
                          includeDetailLines,
                        };
                  polishExperience.mutate(params, {
                    onSuccess: (result) => {
                      setFormPolishResult(
                        (result as { polished: string }).polished,
                      );
                      setFormPolishing(false);
                    },
                    onError: () => {
                      setFormPolishing(false);
                    },
                  });
                }}
                className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2.5 sm:py-1 sm:text-[12px] font-medium text-ai-foreground shadow-sm transition-colors hover:bg-ai/85 focus:outline-none focus:ring-2 focus:ring-ai focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50"
                title="AI polish this description ($0.01)"
              >
                {formPolishing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                AI Polish
              </button>
          </FieldHeader>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={4}
            className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder="Key responsibilities and achievements in this role..."
            aria-invalid={!!fieldErrors.description}
            aria-describedby={
              fieldErrors.description ? 'description-error' : undefined
            }
          />
          {fieldErrors.description && (
            <p
              id="description-error"
              className="mt-1 text-[13px] text-destructive"
            >
              {fieldErrors.description}
            </p>
          )}

          {/* Inline AI polish suggestion in form */}
          {(formPolishResult || formPolishing) && (
            <div className="mt-2 rounded-md border border-ai-border/30 bg-ai-muted px-2.5 py-2">
              <div className="flex items-center gap-1.5">
                {formPolishing ? (
                  <Loader2 className="h-3 w-3 animate-spin text-ai" />
                ) : (
                  <Sparkles className="h-3 w-3 shrink-0 text-ai" />
                )}
                <span className="text-[12px] font-medium uppercase tracking-wide text-ai">
                  {formPolishing ? 'Polishing...' : 'AI suggestion'}
                </span>
              </div>
              {formPolishResult && (
                <>
                  <p className="mt-1 text-[14px] leading-relaxed text-foreground">
                    {formPolishResult}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (!formPolishResult) return;
                        handleChange('description', formPolishResult);
                        setFormPolishResult(null);
                      }}
                      className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-ai-foreground transition-colors hover:bg-ai/90 disabled:opacity-50"
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      Use this
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormPolishing(true);
                        setFormPolishResult(null);
                        const params: {
                          id?: string;
                          description?: string;
                          includeDetailLines?: boolean;
                        } = editingId
                          ? { id: editingId, description: formData.description || undefined, includeDetailLines }
                          : {
                              description: formData.description,
                              includeDetailLines,
                            };
                        polishExperience.mutate(params, {
                          onSuccess: (result) => {
                            setFormPolishResult(
                              (result as { polished: string }).polished,
                            );
                            setFormPolishing(false);
                          },
                          onError: () => {
                            setFormPolishing(false);
                          },
                        });
                      }}
                      disabled={formPolishing}
                      className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                    >
                      <Sparkles className="h-3 w-3" />
                      Regenerate
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormPolishResult(null);
                        setFormPolishing(false);
                      }}
                      className="rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      Dismiss
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {polishExperience.error && !formPolishing && (
            <p className="mt-1 text-[13px] text-destructive">
              {polishExperience.error.message}
            </p>
          )}
          <p className="mt-1.5 text-[13px] text-muted-foreground">
            {(formData.description ?? '').length} / 10,000 characters
          </p>
        </div>

        {/* Detail lines section — inline in the form */}
        {editingId ? (
          <ExperienceDetailLines experienceId={editingId} />
        ) : (
          <PendingDetailLines
            lines={pendingDetails}
            onChange={setPendingDetails}
          />
        )}

        <div className="flex items-center gap-3 border-t border-border pt-4">
          <button
            type="submit"
            disabled={isPending || (!!editingId && !unsaved.hasChanges)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[15px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {editingId ? 'Update' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleCloseForm}
            className="rounded-md px-4 py-2 text-[15px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Cancel
          </button>
        </div>
      </form>
    </FormCard>
  );

  if (experiences.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (experiences.error) {
    return (
      <div>
        <PageHeader title="Work Experience" />
        <div
          className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          Failed to load work experiences. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Work Experience"
        description="Manage your employment history and detail what you did at each job."
      >
        {!isFormOpen && (
          <button
            type="button"
            onClick={openCreateForm}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <Plus className="h-4 w-4" />
            Add Experience
          </button>
        )}
      </PageHeader>

      {/* Bulk action bar */}
      {experienceList.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <label className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <input
              type="checkbox"
              checked={bulk.allSelected}
              ref={(el) => {
                if (el) el.indeterminate = bulk.someSelected;
              }}
              onChange={bulk.toggleAll}
              className="h-3.5 w-3.5 cursor-pointer rounded border-border accent-foreground"
            />
            {inSelectionMode
              ? `${bulk.count} of ${experienceList.length}`
              : 'Select all'}
          </label>
          <BulkActionBar
            selectedCount={bulk.count}
            selectedIds={bulk.selectedArray}
            actions={bulkActions}
            onDeselectAll={bulk.deselectAll}
          />
        </div>
      )}

      {/* Create form — the edit form renders inline in place of the entry being edited */}
      {isFormOpen && !editingId && experienceForm}

      {/* Experience list */}
      {experienceList.length === 0 && !isFormOpen ? (
        <div className="mt-6 rounded-lg border border-dashed border-border p-12 text-center">
          <Briefcase className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 text-[15px] text-muted-foreground">
            No work experience added yet.
          </p>
          <button
            type="button"
            onClick={openCreateForm}
            className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-medium text-primary transition-colors hover:text-primary/80"
          >
            <Plus className="h-3.5 w-3.5" />
            Add your first experience
          </button>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {experienceList.map((experience) => {
            // Render the edit form inline in place of the entry being edited.
            if (editingId === experience.id) {
              return <div key={experience.id}>{experienceForm}</div>;
            }

            const isExpanded = expandedId === experience.id;

            return (
              <div
                key={experience.id}
                className={`group rounded-lg border bg-card transition-colors hover:border-border/80 ${bulk.isSelected(experience.id) ? 'border-primary/40 bg-primary/[0.02]' : 'border-border'}`}
              >
                <div className="flex items-start gap-2 p-3 sm:gap-3 sm:p-4">
                  <SelectionCheckbox
                    checked={bulk.isSelected(experience.id)}
                    onChange={() => bulk.toggle(experience.id)}
                    visible={inSelectionMode}
                    ariaLabel={`Select ${experience.job_title}`}
                  />
                  <button
                    type="button"
                    onClick={() => toggleExpand(experience.id)}
                    className="mt-0.5 shrink-0 text-muted-foreground transition-transform"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="sm:flex sm:items-start sm:justify-between sm:gap-3">
                      <button
                        type="button"
                        onClick={() => toggleExpand(experience.id)}
                        className="block w-full min-w-0 flex-1 text-left focus:outline-none"
                      >
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <h3 className="text-[15px] font-semibold text-foreground">
                            {experience.job_title}
                          </h3>
                          {!experience.end_date && (
                            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[12px] font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-[14px] text-muted-foreground">
                          {experience.company_name}
                        </p>

                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[13px] text-muted-foreground">
                          <span className="font-mono tabular-nums">
                            {experience.start_date}
                            {' \u2013 '}
                            {experience.end_date || 'Present'}
                          </span>
                          {experience.location && (
                            <>
                              <span className="hidden text-border sm:inline">&middot;</span>
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {experience.location}
                              </span>
                            </>
                          )}
                        </div>

                        {!isExpanded && experience.description && (
                          <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground line-clamp-2">
                            {experience.description}
                          </p>
                        )}
                      </button>
                      <div className="shrink-0 flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => openEditForm(experience)}
                          className="rounded-md p-2.5 sm:p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          aria-label={`Edit ${experience.job_title} at ${experience.company_name}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <InlineDeleteConfirm
                          onConfirm={() => handleDelete(experience.id)}
                          isDeleting={deletingId === experience.id}
                          ariaLabel={`Delete ${experience.job_title} at ${experience.company_name}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded detail lines section */}
                {isExpanded && (
                  <div className="px-3 pb-3 sm:px-4 sm:pb-4 sm:pl-10">
                    {experience.description && (
                      <div className="mb-3">
                        <div className="sm:flex sm:items-start sm:justify-between sm:gap-3">
                          <p className="min-w-0 flex-1 text-[14px] leading-relaxed text-muted-foreground">
                            {experience.description}
                          </p>
                          <div className="mt-2 flex shrink-0 items-center gap-2 sm:mt-0">
                            <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
                              <input
                                type="checkbox"
                                checked={!!cardIncludeDetails[experience.id]}
                                onChange={(e) =>
                                  setCardIncludeDetails((prev) => ({
                                    ...prev,
                                    [experience.id]: e.target.checked,
                                  }))
                                }
                                className="h-3 w-3 rounded border-input accent-primary"
                              />
                              Include role highlights
                            </label>
                            <button
                              type="button"
                              onClick={() => handlePolishCard(experience.id)}
                              disabled={polishedTexts[experience.id] === null}
                              className="shrink-0 rounded-md border border-ai-border/40 bg-ai-muted p-2.5 sm:p-1.5 text-ai transition-colors hover:bg-ai hover:text-ai-foreground focus:outline-none focus:ring-2 focus:ring-ai disabled:opacity-50"
                              aria-label="AI polish description"
                              title="AI polish description ($0.01)"
                            >
                              {polishedTexts[experience.id] === null ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Sparkles className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* When no description exists, show generate-from-lines controls */}
                    {!experience.description && (
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                          <input
                            type="checkbox"
                            checked={!!cardIncludeDetails[experience.id]}
                            onChange={(e) =>
                              setCardIncludeDetails((prev) => ({
                                ...prev,
                                [experience.id]: e.target.checked,
                              }))
                            }
                            className="h-3.5 w-3.5 rounded border-input accent-primary"
                          />
                          Include role highlights
                        </label>
                        <button
                          type="button"
                          onClick={() => handlePolishCard(experience.id)}
                          disabled={
                            polishedTexts[experience.id] === null ||
                            !cardIncludeDetails[experience.id]
                          }
                          className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2.5 sm:py-1 sm:text-[12px] font-medium text-ai-foreground shadow-sm transition-colors hover:bg-ai/85 focus:outline-none focus:ring-2 focus:ring-ai focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50"
                          title="Generate description from experience lines ($0.01)"
                        >
                          {polishedTexts[experience.id] === null ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Sparkles className="h-3 w-3" />
                          )}
                          Generate Description
                        </button>
                      </div>
                    )}
                    {/* Inline AI polish suggestion for card description */}
                    {experience.id in polishedTexts && polishedTexts[experience.id] !== undefined && (
                      polishedTexts[experience.id] === null ? (
                        <div className="mb-3 flex items-center gap-1.5 rounded-md border border-ai-border/30 bg-ai-muted px-2.5 py-2">
                          <Loader2 className="h-3 w-3 animate-spin text-ai" />
                          <span className="text-[13px] text-muted-foreground">
                            Polishing...
                          </span>
                        </div>
                      ) : (
                        <div className="mb-3 rounded-md border border-ai-border/30 bg-ai-muted px-2.5 py-2">
                          <div className="flex items-center gap-1.5">
                            <Sparkles className="h-3 w-3 shrink-0 text-ai" />
                            <span className="text-[12px] font-medium uppercase tracking-wide text-ai">
                              AI suggestion
                            </span>
                          </div>
                          <p className="mt-1 text-[14px] leading-relaxed text-foreground">
                            {polishedTexts[experience.id]}
                          </p>
                          <div className="mt-2 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleAcceptPolishCard(experience.id)}
                              disabled={updateExperience.isPending}
                              className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-ai-foreground transition-colors hover:bg-ai/90 disabled:opacity-50"
                            >
                              {updateExperience.isPending ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <CheckCircle2 className="h-3 w-3" />
                              )}
                              Use this
                            </button>
                            <button
                              type="button"
                              onClick={() => handlePolishCard(experience.id)}
                              disabled={polishedTexts[experience.id] === null}
                              className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                            >
                              <Sparkles className="h-3 w-3" />
                              Regenerate
                            </button>
                            <button
                              type="button"
                              onClick={() => dismissPolishCard(experience.id)}
                              className="rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                              Dismiss
                            </button>
                          </div>
                        </div>
                      )
                    )}

                    {/* Inline AI polish error */}
                    {polishCardErrors[experience.id] && (
                      <div className="mt-2 flex items-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-2">
                        <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />
                        <span className="text-[13px] text-destructive">
                          {polishCardErrors[experience.id]}
                        </span>
                      </div>
                    )}

                    <ExperienceDetailLines experienceId={experience.id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete error */}
      {deleteExperience.error && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          {deleteExperience.error.message}
        </div>
      )}

      {/* Discard unsaved changes confirmation */}
      <UnsavedChangesGuard {...unsaved.dialogProps} />
    </div>
  );
}
